import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsletterPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', mobile: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // TEMPORARY: Show popup every time (for testing)
    // Show popup after 5 seconds
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    // TEMPORARY: Commented out for testing - popup will show every time
    // localStorage.setItem('newsletter_popup_seen', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');

    try {
      await axios.post(`${API}/newsletter/subscribe`, formData);
      setMessage('🎉 Success! You\'re subscribed to healthy tips!');
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error) {
      setMessage('❌ Oops! Something went wrong. Try again?');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative animate-slide-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🥗</span>
          </div>
          <h3 className="text-3xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Fredoka, sans-serif'}}>
            Join the Healthy Tribe!
          </h3>
          <p className="text-gray-600">
            Get monthly tips on healthy habits, exercises, and delicious recipes. No spam, just good vibes! 🌱
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-gray-700 font-semibold">Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="Your name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-gray-700 font-semibold">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="mobile" className="text-gray-700 font-semibold">Mobile Number</Label>
            <Input
              id="mobile"
              type="tel"
              placeholder="9876543210"
              pattern="[0-9]{10}"
              value={formData.mobile}
              onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              required
              className="mt-1"
              maxLength={10}
            />
            <p className="text-xs text-gray-500 mt-1">10-digit mobile number</p>
          </div>

          {message && (
            <div className={`text-sm text-center p-3 rounded-lg ${
              message.includes('Success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {message}
            </div>
          )}

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
          >
            {isSubmitting ? 'Subscribing...' : 'Count Me In! 🚀'}
          </Button>

          <button
            type="button"
            onClick={handleClose}
            className="w-full text-gray-500 hover:text-gray-700 text-sm transition-colors"
          >
            Maybe later
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewsletterPopup;
